/**
 * Safe localStorage utilities with error handling and logging
 */

export interface StorageError {
  type: 'quota_exceeded' | 'security' | 'unknown';
  message: string;
}

/**
 * Safely get item from localStorage
 */
export function safeGetItem(key: string): string | null {
  try {
    if (!isLocalStorageAvailable()) return null;
    return localStorage.getItem(key);
  } catch (error) {
    console.warn(`[Storage] Failed to get item "${key}":`, error);
    return null;
  }
}

/**
 * Safely set item in localStorage
 */
export function safeSetItem(key: string, value: string): StorageError | null {
  try {
    if (!isLocalStorageAvailable()) {
      return { type: 'security', message: 'localStorage not available' };
    }
    localStorage.setItem(key, value);
    return null;
  } catch (error) {
    const storageError = error instanceof Error ? error : new Error(String(error));

    // Check for quota exceeded
    if (storageError.name === 'QuotaExceededError') {
      console.error('[Storage] Quota exceeded:', key);
      return { type: 'quota_exceeded', message: 'Storage quota exceeded' };
    }

    console.error(`[Storage] Failed to set item "${key}":`, error);
    return { type: 'unknown', message: storageError.message };
  }
}

/**
 * Safely remove item from localStorage
 */
export function safeRemoveItem(key: string): void {
  try {
    if (isLocalStorageAvailable()) {
      localStorage.removeItem(key);
    }
  } catch (error) {
    console.warn(`[Storage] Failed to remove item "${key}":`, error);
  }
}

/**
 * Safely get JSON from localStorage
 */
export function safeGetJSON<T>(key: string, defaultValue: T): T {
  try {
    const item = safeGetItem(key);
    if (!item) return defaultValue;
    return JSON.parse(item) as T;
  } catch (error) {
    console.warn(`[Storage] Failed to parse JSON from "${key}":`, error);
    return defaultValue;
  }
}

/**
 * Safely set JSON in localStorage
 */
export function safeSetJSON(key: string, value: unknown): StorageError | null {
  try {
    const serialized = JSON.stringify(value);
    return safeSetItem(key, serialized);
  } catch (error) {
    console.error(`[Storage] Failed to stringify value for "${key}":`, error);
    return { type: 'unknown', message: 'Failed to serialize value' };
  }
}

/**
 * Clear all app-specific localStorage, keeping language preference
 */
export function clearAppStorage(): void {
  const keysToKeep = ['i18nextLng'];
  try {
    if (!isLocalStorageAvailable()) return;
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !keysToKeep.includes(key)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => safeRemoveItem(key));
  } catch (error) {
    console.error('[Storage] Failed to clear app storage:', error);
  }
}

/**
 * Check if localStorage is available
 */
function isLocalStorageAvailable(): boolean {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}
